// =====================================================
// IMPORT VALIDATION
// Stage 2: Participants & Pricing
// Validate CSV import data
// =====================================================

export interface ImportValidationError {
  row: number;
  field: string;
  message: string;
}

/**
 * Validate a single row of import data
 */
export function validateImportRow(
  row: Record<string, string>,
  rowNumber: number
): ImportValidationError[] {
  const errors: ImportValidationError[] = [];

  const isMinister = !!row.minister_adb_code;
  const isGuest = !!row.guest_name;

  // Must be either minister or guest
  if (!isMinister && !isGuest) {
    errors.push({
      row: rowNumber,
      field: 'type',
      message: 'Debe proporcionar código ADB (ministro) o nombre (invitado)',
    });
    return errors;
  }

  // Validate minister data
  if (isMinister) {
    const code = row.minister_adb_code?.trim();
    if (!code) {
      errors.push({
        row: rowNumber,
        field: 'minister_adb_code',
        message: 'Código ADB es requerido para ministros',
      });
    } else if (!/^[A-Z]{2,5}\d{2}-\d{3,4}$/i.test(code)) {
      errors.push({
        row: rowNumber,
        field: 'minister_adb_code',
        message: `Formato de código ADB inválido: ${code}. Formato esperado: SOL26-001`,
      });
    }
  }

  // Validate guest data
  if (isGuest) {
    const name = row.guest_name?.trim();
    if (!name) {
      errors.push({
        row: rowNumber,
        field: 'guest_name',
        message: 'Nombre es requerido para invitados',
      });
    } else if (name.length < 3) {
      errors.push({
        row: rowNumber,
        field: 'guest_name',
        message: 'Nombre debe tener al menos 3 caracteres',
      });
    }

    // DNI validation (optional but must be valid if provided)
    const dni = row.guest_dni?.trim();
    if (dni && !/^\d{6,10}$/.test(dni)) {
      errors.push({
        row: rowNumber,
        field: 'guest_dni',
        message: 'DNI debe contener entre 6 y 10 dígitos',
      });
    }

    // Email validation (optional but must be valid if provided)
    const email = row.guest_email?.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push({
        row: rowNumber,
        field: 'guest_email',
        message: 'Email inválido',
      });
    }

    // Phone validation (optional but must be valid if provided)
    const phone = row.guest_phone?.trim();
    if (phone && !/^\d{7,15}$/.test(phone)) {
      errors.push({
        row: rowNumber,
        field: 'guest_phone',
        message: 'Teléfono debe contener entre 7 y 15 dígitos',
      });
    }
  }

  // Validate mandatory flag (optional)
  if (row.is_mandatory !== undefined && row.is_mandatory !== null && row.is_mandatory !== '') {
    const mandatory = String(row.is_mandatory).toLowerCase().trim();
    if (!['true', 'false', '1', '0', 'yes', 'no', 'si', 'sí'].includes(mandatory)) {
      errors.push({
        row: rowNumber,
        field: 'is_mandatory',
        message: 'Valor obligatorio debe ser: true/false, 1/0, yes/no, si/sí',
      });
    }
  }

  return errors;
}

/**
 * Validate all rows of import data
 */
export function validateImportData(
  rows: Record<string, string>[]
): ImportValidationError[] {
  const allErrors: ImportValidationError[] = [];

  rows.forEach((row, index) => {
    const errors = validateImportRow(row, index + 2); // +2 for header row and 1-indexed
    allErrors.push(...errors);
  });

  // Check for duplicate DNIs in guests
  const guestDNIs = new Map<string, number[]>();
  rows.forEach((row, index) => {
    if (row.guest_dni?.trim()) {
      const dni = row.guest_dni.trim();
      if (!guestDNIs.has(dni)) {
        guestDNIs.set(dni, []);
      }
      guestDNIs.get(dni)?.push(index + 2);
    }
  });

  guestDNIs.forEach((rowNumbers, dni) => {
    if (rowNumbers.length > 1) {
      rowNumbers.forEach(rowNumber => {
        allErrors.push({
          row: rowNumber,
          field: 'guest_dni',
          message: `DNI duplicado en el archivo: ${dni}`,
        });
      });
    }
  });

  return allErrors;
}
